import { useState } from 'react';
import {
  Code,
  Copy,
  Check,
  Layout,
  ShoppingBag,
} from 'lucide-react';

export default function BuyButtonPage() {
  const [copied, setCopied] = useState(false);
  const [layoutType, setLayoutType] = useState<'button' | 'card'>('card');
  const [buttonColor, setButtonColor] = useState('#2563EB');
  const [buttonText, setButtonText] = useState('Add to Cart');
  const [selectedProduct, setSelectedProduct] = useState('Organic Moroccan Argan Oil (100ml)');

  const embedCode = `<!-- Dukene Buy Button Embed -->
<div id="dukene-buy-button-container"></div>
<script type="text/javascript">
  (function () {
    var script = document.createElement('script');
    script.async = true;
    script.src = 'https://embed.dukene.com/buy-button/v1/widget.js';
    document.head.appendChild(script);
    script.onload = function () {
      DukeneBuyButton.init({
        id: '294819024',
        layout: '${layoutType}',
        buttonColor: '${buttonColor}',
        buttonText: '${buttonText}'
      });
    };
  })();
</script>`;

  const handleCopyCode = () => {
    navigator.clipboard.writeText(embedCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 pb-16">
      {/* Page Header */}
      <div className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-indigo-50 dark:bg-indigo-950/40 border border-indigo-200 dark:border-indigo-800/40 flex items-center justify-center text-indigo-600 dark:text-indigo-400">
                <Code className="w-5 h-5" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-xl font-bold text-slate-900 dark:text-white">Buy Button</h1>
                  <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300">
                    Channel Connected
                  </span>
                </div>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Create embeddable shoppable product cards and checkout buttons for any website, blog, or CMS
                </p>
              </div>
            </div>

            <button
              onClick={handleCopyCode}
              className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-medium text-sm rounded-xl shadow-sm transition-all"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? 'Copied Snippet!' : 'Copy Embed Code'}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Customizer Controls (Left 5 Columns) */}
          <div className="lg:col-span-5 space-y-6">
            <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm p-6 space-y-6">
              <div>
                <h3 className="text-base font-bold text-slate-900 dark:text-white">Widget Customizer</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Configure widget appearance, target product, and call-to-action text
                </p>
              </div>

              {/* Product Selector */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-2">
                  Target Product
                </label>
                <select
                  value={selectedProduct}
                  onChange={(e) => setSelectedProduct(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-white"
                >
                  <option>Organic Moroccan Argan Oil (100ml) - $38.00</option>
                  <option>Vitamin C Brightening Serum (30ml) - $42.00</option>
                  <option>Silk Pillowcase Luxury Set - $65.00</option>
                </select>
              </div>

              {/* Layout Type */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-2">
                  Embed Layout Style
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setLayoutType('card')}
                    className={`p-3 rounded-xl border text-left transition-all ${
                      layoutType === 'card'
                        ? 'border-indigo-600 bg-indigo-50/50 dark:bg-indigo-950/30'
                        : 'border-slate-200 dark:border-slate-800 hover:border-slate-300'
                    }`}
                  >
                    <Layout className="w-5 h-5 text-indigo-600 mb-1" />
                    <p className="text-sm font-semibold text-slate-900 dark:text-white">Product Card</p>
                    <p className="text-xs text-slate-500">Image, title, price & button</p>
                  </button>

                  <button
                    type="button"
                    onClick={() => setLayoutType('button')}
                    className={`p-3 rounded-xl border text-left transition-all ${
                      layoutType === 'button'
                        ? 'border-indigo-600 bg-indigo-50/50 dark:bg-indigo-950/30'
                        : 'border-slate-200 dark:border-slate-800 hover:border-slate-300'
                    }`}
                  >
                    <ShoppingBag className="w-5 h-5 text-indigo-600 mb-1" />
                    <p className="text-sm font-semibold text-slate-900 dark:text-white">Basic Button</p>
                    <p className="text-xs text-slate-500">Standalone CTA button only</p>
                  </button>
                </div>
              </div>

              {/* Button Text */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-2">
                  Button Label
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {['Add to Cart', 'Buy Now', 'Shop This Item', 'Order Online'].map((txt) => (
                    <button
                      key={txt}
                      type="button"
                      onClick={() => setButtonText(txt)}
                      className={`px-3 py-2 rounded-lg text-xs font-medium border transition-all ${
                        buttonText === txt
                          ? 'border-indigo-600 bg-indigo-600 text-white'
                          : 'border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      {txt}
                    </button>
                  ))}
                </div>
              </div>

              {/* Accent Color */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-2">
                  Button Color
                </label>
                <div className="flex items-center gap-3">
                  {[
                    { hex: '#2563EB', name: 'Blue' },
                    { hex: '#111827', name: 'Dark' },
                    { hex: '#059669', name: 'Emerald' },
                    { hex: '#7C3AED', name: 'Violet' },
                    { hex: '#DC2626', name: 'Red' },
                  ].map((color) => (
                    <button
                      key={color.hex}
                      type="button"
                      onClick={() => setButtonColor(color.hex)}
                      className={`w-8 h-8 rounded-full border-2 transition-transform ${
                        buttonColor === color.hex ? 'scale-110 border-slate-900 dark:border-white' : 'border-transparent'
                      }`}
                      style={{ backgroundColor: color.hex }}
                      title={color.name}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Live Preview & Code Output (Right 7 Columns) */}
          <div className="lg:col-span-7 space-y-6">
            {/* Live Preview Box */}
            <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm p-6">
              <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-200 dark:border-slate-800">
                <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Live Embed Preview</span>
                <span className="text-xs text-slate-400">Responsive HTML5 Widget</span>
              </div>

              <div className="min-h-[260px] flex items-center justify-center p-6 bg-slate-100/70 dark:bg-slate-950/60 rounded-2xl border border-dashed border-slate-300 dark:border-slate-800">
                {layoutType === 'card' ? (
                  <div className="w-full max-w-sm bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-5 shadow-lg space-y-4">
                    <div className="w-full h-40 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-400 text-sm font-medium">
                      Product Image Preview
                    </div>
                    <div>
                      <h4 className="text-base font-bold text-slate-900 dark:text-white">{selectedProduct}</h4>
                      <p className="text-sm font-semibold text-slate-600 dark:text-slate-400 mt-1">$38.00 USD</p>
                    </div>
                    <button
                      type="button"
                      style={{ backgroundColor: buttonColor }}
                      className="w-full py-3 px-4 rounded-xl text-white font-semibold text-sm shadow-sm hover:opacity-95 transition-opacity"
                    >
                      {buttonText}
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    style={{ backgroundColor: buttonColor }}
                    className="py-3 px-8 rounded-xl text-white font-semibold text-sm shadow-md hover:opacity-95 transition-opacity"
                  >
                    {buttonText} — $38.00
                  </button>
                )}
              </div>
            </div>

            {/* Generated Code Output */}
            <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between bg-slate-50/50 dark:bg-slate-900/50">
                <div>
                  <h3 className="text-sm font-bold text-slate-900 dark:text-white">Generated Embed Code</h3>
                  <p className="text-xs text-slate-500">Paste anywhere inside your page &lt;body&gt; tags</p>
                </div>
                <button
                  onClick={handleCopyCode}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-indigo-50 dark:bg-indigo-950/50 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-100"
                >
                  {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied ? 'Copied!' : 'Copy Code'}
                </button>
              </div>

              <div className="p-4 bg-slate-900 text-slate-200 overflow-x-auto text-xs font-mono">
                <pre>{embedCode}</pre>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
